import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LogIn, LogOut, MapPin, AlertTriangle, CheckCircle2, Clock, ArrowRight, Loader2, Home } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import errorToast from "@/lib/errorToast";

/**
 * PunchCard — large home-dashboard CTA for daily attendance punch.
 *
 * States:
 *  - "off"      : no punch yet today → PUNCH IN
 *  - "in"       : punched in, no out yet → PUNCH OUT + elapsed
 *  - "complete" : punched in AND out at least once → DAY COMPLETE with total hours
 *
 * Honors per-role geofence enforcement via the existing API.
 * Skips render if user has no employee shift (i.e. backend says no shift configured).
 */
export function PunchCard() {
  const { t } = useTranslation();
  const me = useAuth((s) => s.user);
  const isCEO = me && (me.role || "").toUpperCase() === "CEO";
  const [today, setToday] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [posError, setPosError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [justPunched, setJustPunched] = useState(null); // "in" | "out" | null
  // D23 -- WFH-aware rendering. If the current user has an approved
  // WFH request covering today, we (a) swap the punch button label to
  // "PUNCH IN (WFH)" (blue, not the default gradient), (b) hide the
  // geofence indicator since geofence is disabled for WFH days per
  // D20, and (c) surface a small "Working from home today" caption.
  const [myWfh, setMyWfh] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setToday(data);
    } catch (_e) { /* silent — punch card is non-critical */ }
    // D23 -- fetch WFH-today roster and see if `me.id` is on it. The
    // /wfh/today endpoint returns the whole company roster; filtering
    // client-side keeps the API surface unchanged.
    try {
      const { data } = await api.get("/wfh/today", { silent: true });
      const mine = (data?.rows || []).find((r) => r.user_id === me?.id);
      setMyWfh(mine || null);
    } catch (_e) { setMyWfh(null); }
  }, [me?.id]);

  useEffect(() => { load(); }, [load]);

  // Geolocation watcher
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => { setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }); setPosError(null); },
      (e) => setPosError(e.message || "Location unavailable"),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Live elapsed counter
  const lastPunch = today?.attendance?.punches?.[today?.attendance?.punches.length - 1];
  const isPunchedIn = Boolean(lastPunch && !lastPunch.out_time);
  useEffect(() => {
    if (!isPunchedIn || !lastPunch?.in_time) return;
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(lastPunch.in_time).getTime()) / 1000));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [isPunchedIn, lastPunch?.in_time]);

  if (today === null) return null;

  const totalSec = today?.attendance?.total_seconds || 0;
  const totalHours = today?.attendance?.total_hours || 0;
  const hasAnyPunch = (today?.attendance?.punches?.length || 0) > 0;
  const state = !hasAnyPunch ? "off" : isPunchedIn ? "in" : "complete";

  // Build status line
  const fmtHm = (s) => `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  const inTime = lastPunch?.in_time?.slice(11, 16);
  const status = state === "off"      ? "Not yet punched in today"
              :  state === "in"       ? `Punched in at ${inTime} IST · ${fmtHm(elapsed)} elapsed`
              :                          `Day complete: ${fmtHm(totalSec || totalHours * 3600)}`;

  const handlePunch = async (kind) => {
    // D23 -- WFH days skip the geofence gate. The backend already
    // disables geofence enforcement when the punch matches an
    // approved WFH; sending `lat=0/lng=0` with a "wfh" hint is
    // silently accepted. If we do have real geolocation we still
    // send it (useful for audit / fraud detection later).
    if (!myWfh && !myPos) {
      toast.error(posError || t("attendance.noLocation")); return;
    }
    setBusy(true);
    try {
      const body = myWfh
        ? { lat: myPos?.lat ?? 0, lng: myPos?.lng ?? 0, accuracy: myPos?.acc ?? 0, wfh: true }
        : { lat: myPos.lat, lng: myPos.lng, accuracy: myPos.acc };
      const { data } = await api.post(`/attendance/${kind}`, body);
      setToday((s) => ({ ...s, attendance: data.attendance }));
      setJustPunched(kind === "punch-in" ? "in" : "out");
      setTimeout(() => setJustPunched(null), 1800);
      if (myWfh) {
        toast.success(kind === "punch-in" ? "Punched in (WFH)" : "Punched out (WFH)");
      } else if (data.geofence?.applied && !data.geofence.is_inside) {
        toast.warning(`Outside geofence (${data.geofence.distance_m}m). Logged with flag.`);
      } else {
        toast.success(kind === "punch-in" ? "Punched in" : "Punched out");
      }
    } catch (e) {
      errorToast(e);
    } finally { setBusy(false); }
  };

  // Visual gradient by state
  const STATE_GRADIENTS = {
    off:      "linear-gradient(135deg, #003771 0%, #1E96FC 100%)",
    in:       "linear-gradient(135deg, #c2410c 0%, #f59e0b 100%)",
    complete: "linear-gradient(135deg, #14532d 0%, #16a34a 100%)",
  };

  // D36.c -- CEO does not track attendance. Render a small placeholder
  // card so the dashboard grid keeps its rhythm.
  if (isCEO) {
    return (
      <div className="glass-elevated p-4 mb-6 flex items-center gap-3"
           data-testid="home-punch-card-ceo">
        <div className="h-10 w-10 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300 text-lg">★</div>
        <div>
          <div className="font-heading text-sm uppercase tracking-[0.16em] text-amber-200">Owner / Executive</div>
          <div className="text-[11px] text-foreground/55">Attendance is not tracked for the CEO role.</div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      data-testid="home-punch-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="relative glass-elevated overflow-hidden mb-6"
      data-tour-id="punch-card"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: STATE_GRADIENTS[state] }} />
      <div className="relative p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={
              state === "in"       ? "border-amber-500/40 bg-amber-500/10 text-amber-300" :
              state === "complete" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" :
                                     "border-sky-500/40 bg-sky-500/10 text-sky-300"
            }>{state === "in" ? "WORKING" : state === "complete" ? "DAY COMPLETE" : "OFF-CLOCK"}</Badge>
            {today?.shift && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />{today.shift.name} · {today.shift.start}–{today.shift.end}
              </span>
            )}
          </div>
          <div className="font-heading text-2xl sm:text-3xl uppercase tracking-[0.06em] font-extrabold leading-tight">
            {state === "off" && "Mark your presence"}
            {state === "in" && `On the clock · ${fmtHm(elapsed)}`}
            {state === "complete" && `Done for today · ${fmtHm(totalSec || totalHours * 3600)}`}
          </div>
          <div className="text-xs text-foreground/65 mt-1.5 flex items-center gap-1.5">
            {state === "complete" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> :
             state === "in"       ? <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" /> :
                                     <MapPin className="h-3.5 w-3.5" />}
            {status}
          </div>
          <div className="text-[11px] text-foreground/50 mt-1 flex items-center gap-1.5">
            {myWfh ? (
              <span className="text-sky-300 inline-flex items-center gap-1"
                    data-testid="punch-wfh-badge">
                <Home className="h-3 w-3" /> Working from home today
                {myWfh.request_id && <span className="text-foreground/45 ml-1">· {myWfh.request_id}</span>}
              </span>
            ) : posError
              ? <span className="text-rose-300/80 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{posError}</span>
              : myPos
                ? <span>📍 {myPos.lat.toFixed(5)}, {myPos.lng.toFixed(5)} · ±{Math.round(myPos.acc || 0)}m</span>
                : <span>Locating…</span>}
            <Link to="/hr/attendance" className="ml-auto text-sky hover:underline inline-flex items-center gap-0.5" data-testid="punch-view-log">
              View full attendance log <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        <div className="flex sm:justify-end">
          {state === "off" && (
            <Button
              data-testid="home-punch-in-btn"
              disabled={busy || (!myWfh && !myPos)}
              onClick={() => handlePunch("punch-in")}
              className={myWfh
                ? "h-14 px-7 text-white font-heading tracking-[0.18em] text-base shadow-lg bg-sky-500 hover:bg-sky-600"
                : "h-14 px-7 bg-gradient-primary text-white font-heading tracking-[0.18em] text-base shadow-lg"}
            >
              {myWfh
                ? <><Home className="h-5 w-5 mr-2" /> PUNCH IN (WFH)</>
                : <><LogIn className="h-5 w-5 mr-2" /> PUNCH IN</>}
            </Button>
          )}
          {state === "in" && (
            <div className="relative">
              <AnimatePresence>
                {justPunched === "in" && (
                  <>
                    <motion.span
                      key="ring1"
                      className="pulse-ring"
                      initial={{ opacity: 0.65 }}
                      animate={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                    />
                    <motion.div
                      key="check-burst"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: [0, 1.4, 1], opacity: [0, 1, 0] }}
                      transition={{ duration: 1.4, ease: "easeOut" }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                      data-testid="punch-success-burst"
                    >
                      <CheckCircle2 className="h-8 w-8 text-sky-300 drop-shadow-[0_0_12px_rgba(30,150,252,0.8)]" />
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
              <Button
                data-testid="home-punch-out-btn"
                disabled={busy}
                onClick={() => handlePunch("punch-out")}
                className="h-14 px-7 text-white font-heading tracking-[0.18em] text-base shadow-lg relative z-10"
                style={{ background: "linear-gradient(135deg, #c2410c 0%, #f59e0b 100%)" }}
              >
                <LogOut className="h-5 w-5 mr-2" /> PUNCH OUT
              </Button>
            </div>
          )}
          {state === "complete" && (
            <div className="text-center" data-testid="punch-complete-card">
              <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center" style={{ background: STATE_GRADIENTS.complete }}>
                <CheckCircle2 className="h-7 w-7 text-white" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/70 mt-2">See you tomorrow</div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default PunchCard;
